import * as a from '@mitranim/js/all.mjs'
import * as c from './conf.mjs'
import * as ut from './util-text.mjs'
import * as uti from './util-time.mjs'
import * as um from './util-misc.mjs'
import * as uc from './util-coll.mjs'
import * as uf from './util-form.mjs'
import * as uva from './util-valid.mjs'
import * as us from './util-sel.mjs'
import * as dmi from './dat-misc.mjs'
import * as dfs from './dat-form-spec.mjs'

export class FormSpeccerService extends dfs.FormSpeccer {
  getForm() {
    return new ServiceForm({
      entity: this.ent,
      fieldSpecs: this.getFieldSpecs(),
    })
  }

  preinit({id, nature, hasPaidListing}) {
    this.id = a.optPk(id)
    this.nature = a.reqValidStr(nature)
    this.hasPaidListing = a.laxBool(hasPaidListing)
  }

  async init() {
    await um.plist()
      .add(this.initEnt())
      .add(this.initCategories())
      .add(this.initLanguageSelectables())
      .add(this.initStyleSelectables())
      .add(this.initNatureSelectables())
      .add(this.initGrowthTypeSelectables())
      // .add(this.initPriceValidator()) // Cause: Allow make any services without `PaidListing`.
  }

  async initEnt() {this.ent = await this.fetchEnt(this.id)}

  async fetchEnt() {
    const id = this.id
    return a.vac(id) && await this.ctx.dat.serviceLatest(id).valueTaskFresh()
  }

  // Overridden by some subclasses.
  recompute = undefined

  getFieldSpecs() {
    return uc.vacOf(
      this.getFieldSpecImageId(),
      this.getFieldSpecTitle(),
      this.getFieldSpecDescription(),
      this.getFieldSpecPriceCents(),
      this.getFieldSpecMainCategoryIds(),
      this.getFieldSpecConcernCategoryIds(),
      this.getFieldSpecDurationMinutes(),
      this.getFieldSpecParticipantAgeMin(),
      this.getFieldSpecStyle(),
      this.getFieldSpecNature(),
      this.getFieldSpecRelationType(),
      this.getFieldSpecMaxBookings(),
      this.getFieldSpecGrowthType(),
      this.getFieldSpecIsOnline(),
      this.getFieldSpecGeoName(),
      this.getFieldSpecLanguageCode(),
    )
  }

  getFieldSpecImageId() {
    return {
      name: `imageId`,
      type: c.UI_FIELD_TYPE_IMAGE_FILE,
      title: `Cover Image`,
      fieldProps: {
        labelSubTitle: [`Supported file formats: ${ut.extsToFileInputAccept(c.IMAGE_EXTS)}`],
      },
      value: this.ent?.imageId,
      validators: [uva.validateFieldImageFileNotEmpty],
      related: this.ent?.image,
    }
  }

  getFieldSpecTitle() {
    return {
      name: `title`,
      type: c.UI_FIELD_TYPE_TEXT,
      title: `Title`,
      value: this.ent?.title,
      inputProps: {maxLength: 80},
      fieldProps: {
        labelWarning: a.spaced(
          `Please note that links and contact details are not allowed in your service/video description.`,
          `Violation of our rules may result in profile termination.`
        ),
      },
      validators: [uva.validateFieldNotEmpty],
    }
  }

  getFieldSpecDescription() {
    return {
      name: `description`,
      type: c.UI_FIELD_TYPE_TEXT_MD,
      title: `Description`,
      value: this.ent?.description || serviceContentInit(this.nature),
      validators: [uva.validateFieldNotEmpty],
    }
  }

  async initPriceValidator() {
    this.priceValidator = a.vac(!this.hasPaidListing) && await uva.validatorFieldNotFreePrice()
  }

  getFieldSpecPriceCents() {
    return {
      name: `priceCents`,
      type: c.UI_FIELD_TYPE_CENTS_WITH_RADIO,
      title: `Price in USD`,
      value: this.ent?.priceCents,
      validators: uc.vacOf(
        uva.validateFieldNotEmpty,
        this.priceValidator,
      ),
      fieldProps: {
        labelWarning: a.spaced(
          `Please note that the price must be set in United States Dollars. You will be able to receive`,
          `the payment in another currency at the exchange rate of the day when the service/video is provided.`,
          `The price you set for your services is the amount you will receive in full. However, please be aware`,
          `that clients may see a slightly higher price, which includes a transaction fee to cover payment`,
          `processing costs. This ensures a smooth and secure experience for both you and your clients,`,
          `without affecting your earnings.`,
        ),
        labelSubTitle: this.priceCentsLabel(),
      },
      inputProps: {
        selectables: [
          {value: 0, title: `FREE`},
        ],
      },
    }
  }

  priceCentsLabel() {
    if (!c.MIN_SERVICE_PRICE) return undefined
    return [
      `If this is your first service on `, c.BRAND_NAME, `, please set the price of ${c.MIN_SERVICE_PRICE}. `,
      `This will allow you to have initial bookings, receive reviews, and start selling more expensive services.`
    ]
  }

  async initCategories() {
    this.categories = await this.ctx.dat.categories.valueTask()
  }

  getFieldSpecMainCategoryIds() {
    return dfs.fieldSpecMainCategoryIds({
      cats: this.categories,
      max: c.MAX_ENTITY_CATEGORIES,
      value: this.ent?.mainCategoryIds,
    })
  }

  getFieldSpecConcernCategoryIds() {
    return dfs.fieldSpecConcernCategoryIds({
      cats: this.categories,
      max: c.MAX_ENTITY_CONCERNS,
      value: this.ent?.concernCategoryIds,
    })
  }

  getFieldSpecDurationMinutes() {
    return {
      name: `durationMinutes`,
      type: c.UI_FIELD_TYPE_NUMBER_WITH_RADIO,
      title: `Duration in Minutes`,
      value: a.optNum(this.ent?.duration) && uti.secsToMins(this.ent?.duration),
      validators: [uva.validateFieldNotEmpty],
      inputProps: {
        placeholder: `Custom`,
        selectables: [
          {value: 15, title: `15m`},
          {value: 30, title: `30m`},
          {value: 45, title: `45m`},
          {value: 60, title: `60m`},
        ],
      },
    }
  }

  getFieldSpecParticipantAgeMin() {
    return {
      name: `participantAgeMin`,
      type: c.UI_FIELD_TYPE_SELECT,
      multiple: false,
      title: `Age Restriction`,
      value: this.ent?.participantAgeMin,
      validators: [uva.validateFieldNotEmpty],
      inputProps: {selectables: c.SERVICE_PARTICIPANT_AGE_SELECTABLES},
    }
  }

  async initStyleSelectables() {
    this.styleSelectables = await this.ctx.dat.serviceStyleSelectables.valueTask()

    /* Prevent of new courses create as services. */
    this.styleSelectables = a.filter(this.styleSelectables, val => val.value !== `course`)
  }

  getFieldSpecStyle() {
    return {
      name: `style`,
      type: c.UI_FIELD_TYPE_SELECT,
      multiple: false,
      title: `Service Type`,
      value: this.ent?.style,
      validators: [uva.validateFieldNotEmpty],
      recompute: this.recompute,
      inputProps: {selectables: this.styleSelectables},
    }
  }

  async initNatureSelectables() {
    this.natureSelectables = await this.ctx.dat.serviceNatureSelectables.valueTask()
  }

  getFieldSpecNature() {
    const nature = a.reqValidStr(this.nature)
    const allowed = us.selsByVals(this.natureSelectables, [nature])

    // Not supposed to happen.
    if (a.isEmpty(allowed)) return undefined

    if (a.len(allowed) === 1) {
      const value = a.head(allowed).value
      return uf.fieldSpecHidden(`nature`, value)
    }

    return {
      name: `nature`,
      type: c.UI_FIELD_TYPE_RADIO,
      title: `Session or Event`,
      value: this.ent?.nature,
      validators: [uva.validateFieldNotEmpty],
      recompute: this.recompute,
      inputProps: {
        direction: `vertical`,
        circular: true,
        selectables: allowed,
      },
    }
  }

  getFieldSpecRelationType() {}
  getFieldSpecMaxBookings() {}

  async initGrowthTypeSelectables() {
    this.growthTypeSelectables = await this.ctx.dat.serviceGrowthTypeSelectables.valueTask()
  }

  getFieldSpecGrowthType() {
    return {
      name: `growthType`,
      type: c.UI_FIELD_TYPE_RADIO,
      title: `Growth Type`,
      value: this.ent?.growthType,
      validators: [uva.validateFieldNotEmpty],
      recompute: this.recompute,
      fieldProps: {
        labelWarning: a.spaced(
          `Professional growth means that your client will be able to practice or will get a new qualification. For example, after reading a book, they will become a professional tarot reader or a professional fitness instructor. `,
          `A personal growth book is a book that aims to help individuals improve their mental, emotional, physical, and spiritual well-being in some way.`
        ),
      },
      inputProps: {
        direction: `vertical`,
        circular: true,
        selectables: this.growthTypeSelectables,
      },
    }
  }

  getFieldSpecIsOnline() {}
  getFieldSpecGeoName() {}

  async initLanguageSelectables() {
    this.languageSelectables = await this.ctx.dat.languageSelectables.valueTask()
  }

  getFieldSpecLanguageCode() {
    return {
      name: `languageCode`,
      type: c.UI_FIELD_TYPE_SELECT,
      multiple: false,
      title: `Language`,
      value: this.ent?.language,
      validators: [uva.validateFieldNotEmpty],
      inputProps: {
        placeholder: `Language`,
        selectables: this.languageSelectables,
      },
    }
  }
}

export class FormSpeccerBookableService extends FormSpeccerService {
  recompute = recomputeServiceFormField

  async init() {
    await um.plist()
      .add(super.init())
      .add(this.initRelationTypeSelectables())
  }

  async initRelationTypeSelectables() {
    this.relationTypeSelectables = await this.ctx.dat.serviceRelationTypeSelectables.valueTask()
  }

  getFieldSpecRelationType() {
    return {
      name: `relationType`,
      type: c.UI_FIELD_TYPE_RADIO,
      title: `Number of Participants`,
      value: this.ent?.relationType,
      validators: [uva.validateFieldNotEmpty],
      recompute: this.recompute,
      inputProps: {
        direction: `vertical`,
        circular: true,
        selectables: this.relationTypeSelectables,
      },
    }
  }

  getFieldSpecMaxBookings() {
    return {
      name: `maxBookings`,
      type: c.UI_FIELD_TYPE_NUMBER,
      title: `Maximum Participants (use 0 for unlimited)`,
      value: this.ent?.maxBookings,
      recompute(field) {
        const isGroup = dmi.isServiceGroup({relationType: field.form.fields.relationType?.getValueSingle()})
        if (isGroup) return
        field.disable()
        field.setValueAny(0)?.trigger()
      },
    }
  }

  getFieldSpecIsOnline() {
    return {
      name: `isOnline`,
      type: c.UI_FIELD_TYPE_RADIO,
      title: `Online or Offline`,
      value: this.ent?.isOnline,
      validators: [uva.validateFieldNotEmpty],
      recompute: this.recompute,
      fieldProps: {
        labelWarning: a.spaced(
          `When you choose to provide an offline service, it means that the client will have to arrive to your office in your city.`,
          `An online service can be provided to anyone in the world as you will connect with your client via a video platform.`,
        ),
      },
      inputProps: {
        direction: `vertical`,
        circular: true,
        selectables: c.SERVICE_ONLINE_SELECTABLES,
      },
    }
  }

  getFieldSpecGeoName() {
    return {
      name: `geoName`,
      type: c.UI_FIELD_TYPE_GEO_NAME,
      title: `Geographic Location`,
      value: this.ent?.geoName,
      validators: [uva.validateServiceGeoFieldOpt],
      coordName: `geoPoint`,
      coordValue: this.ent?.geoPoint,
      recompute(field) {
        if (field.form.fields.isOnline?.getValueSingle()) {
          field.disable()
          field.clear()?.trigger()
        }
        else {
          field.enable()
        }
      },
      groupTitle: `Geo`,
    }
  }
}

export class FormSpeccerServiceSession extends FormSpeccerBookableService {
  initLanguageSelectables() {}
  getFieldSpecLanguageCode() {}
}

export class FormSpeccerServiceEvent extends FormSpeccerBookableService {}

export class FormSpeccerAccessService extends FormSpeccerService {
  getFieldSpecRelationType() {
    return uf.fieldSpecHidden(`relationType`, c.SERVICE_RELATION_TYPE_ONE_TO_MANY)
  }

  getFieldSpecIsOnline() {return uf.fieldSpecHidden(`isOnline`, true)}
}

export class FormSpeccerServiceVideo extends FormSpeccerAccessService {
  recompute = recomputeServiceFormField
  priceCentsLabel() {}
}

export class FormSpeccerServiceBook extends FormSpeccerAccessService {
  getFieldSpecs() {
    return uc.vacOf(
      this.getFieldSpecBookFileIds(),
      ...super.getFieldSpecs(),
    )
  }

  getFieldSpecBookFileIds() {
    return {
      name: `bookFileIds`,
      type: c.UI_FIELD_TYPE_BOOK_FILE,
      title: `Book Files (${ut.extsToFileInputAccept(c.BOOK_EXTS)})`,
      multiple: true,
      value: this.ent?.bookFileIds,
      related: this.ent?.bookFiles,
      validators: [
        uva.validateFieldBookFileNotEmpty,
        uva.validateFileExt(c.BOOK_EXTS),
      ],
    }
  }

  getFieldSpecDurationMinutes() {}
  initStyleSelectables() {}
  getFieldSpecStyle() {}
}

export class FormSpeccerServiceCourse extends FormSpeccerAccessService {
  initStyleSelectables() {}
  getFieldSpecStyle() {}
  initNatureSelectables() {}
  getFieldSpecNature() {return uf.fieldSpecHidden(`nature`, c.SERVICE_NATURE_COURSE)}
}

/*
Missing feature: field for additional files. Similar to book files for book
services, but it needs to use a new generic field for arbitrary files.
*/
export class FormSpeccerServiceCourseSectionBase extends FormSpeccerAccessService {
  preinit(src) {
    this.parentServiceOriginId = a.reqValidStr(src.parentServiceOriginId)
    return super.preinit(src)
  }

  fetchEnt() {
    // Required for `.getFieldSpecFileIds`.
    this.ctx.pageDat.inp.get.sets.files.enable()
    return super.fetchEnt()
  }

  getFieldSpecs() {
    return uc.vacOf(
      this.getFieldSpecParentServiceOriginId(),
      ...super.getFieldSpecs(),
      this.getFieldSpecFileIds(),
    )
  }

  getFieldSpecParentServiceOriginId() {
    return uf.fieldSpecHidden(`parentServiceOriginId`, (
      this.parentServiceOriginId || this.ent?.parentServiceOriginId
    ))
  }

  getFieldSpecFileIds() {
    return {
      name: `fileIds`,
      type: c.UI_FIELD_TYPE_FILE,
      title: `Course Section Materials`,
      multiple: true,
      value: this.ent?.fileIds,
      related: this.ent?.files,
      fieldProps: {
        labelWarning: a.spaced(
          `Upload all the files for this section of your course. These can include videos, images, and text files.`,
          `Your students will view the files in the order you upload them, so please name the files clearly to`,
          `indicate their content.`
        ),
      },
    }
  }

  initCategories() {}
  getFieldSpecMainCategoryIds() {}
  getFieldSpecConcernCategoryIds() {}

  initLanguageSelectables() {}
  getFieldSpecLanguageCode() {}

  initStyleSelectables() {}
  getFieldSpecStyle() {return uf.fieldSpecHidden(`style`, c.SERVICE_STYLE_COURSE)}

  initNatureSelectables() {}
  getFieldSpecNature() {return uf.fieldSpecHidden(`nature`, c.SERVICE_NATURE_SUB)}

  initPriceValidator() {}
  getFieldSpecPriceCents() {}
}

export class FormSpeccerServiceCourseSectionCreate extends FormSpeccerServiceCourseSectionBase {}

export class FormSpeccerServiceCourseSectionEdit extends FormSpeccerServiceCourseSectionBase {}

function serviceContentInit(nature) {
  nature = a.words(nature).titleSpaced()
  if (!nature) return ``

  return `
<!-- Write a short overview of your service. It will be shown on the preview. -->
<!-- (up to 20 words) -->

## Overview
<!-- What is it in general? Please describe the ${nature} in 2-3 sentences. -->

## Target audience
<!-- Who is this ${nature} intended for? -->

## Benefits
<!-- What are the benefits of the ${nature}? -->

## ${nature}
<!-- What to expect during the ${nature}? -->

## Other comments
<!-- Any other comments about you or the ${nature}. -->
`.trim()
}

/*
Allows service fields to affect each other. Selecting a field may disable
another field or restrict possible values.

Rules:

  * When the user modifies a field, recomputation shouldn't affect that field,
    only other fields.

  * If the resulting set of selectables for the current field is empty, the
    field is cleared and disabled.
*/
function recomputeServiceFormField(field) {
  const value = field.getValueSingle()
  const selectables = serviceFormFieldAllowedSelectables(field)

  const isSelected = a.some(
    selectables,
    function test(sel) {return a.is(value, sel.value)},
  )

  field.selectables = selectables
  field.disabled = a.isEmpty(selectables)
  if (field.hasValue() && !isSelected) field.clear()?.trigger()
}

function serviceFormFieldAllowedValues(field) {
  const name = uf.reqFieldName(field)
  const {fields} = field.form

  function testPattern([key, vals]) {
    if (key === name) return true
    const val = fields[key]?.onlyEnabled()?.getValueSingle()
    return a.isNil(val) || a.includes(vals, val)
  }

  const allowedOptions = a.filter(
    c.SERVICE_OPTIONS,
    function testPatterns(pattern) {
      return a.every(a.entries(pattern), testPattern)
    },
  )

  return uc.uniq(a.mapFlat(allowedOptions, opt => opt[name]))
}

function serviceFormFieldAllowedSelectables(field) {
  const vals = serviceFormFieldAllowedValues(field)
  return a.filter(
    field.specSelectables(),
    function test(sel) {return a.includes(vals, sel?.value)},
  )
}

class ServiceForm extends uf.Form {
  fromEntity(src) {
    src = Object.create(super.fromEntity(src))
    src.durationMinutes = uti.secsToMins(src.duration)
    src.priceCurrency ||= c.CURRENCY_DEFAULT.code
    return src
  }

  async toEntity(ctx) {
    const {durationMinutes, priceCurrency, ...rest} = await super.toEntity(ctx)
    return {
      ...rest,
      duration: uti.minsToSecs(durationMinutes),
      priceCurrency: priceCurrency || c.CURRENCY_DEFAULT.code,
    }
  }
}
